import { useEffect, useState } from "react";
import { Calendar, Mail, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../../../lib/supabase";

interface Mentor {
  id: string;
  name: string;
  email: string;
}

interface RawMentorData {
  uuid: string;
  vincer_base_user: {
    name: string;
    email: string;
  };
}

interface RawAssignmentData {
  mentor_uuid: string;
  mentor: RawMentorData;
}

export function MentorsPage() {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMentors() {
      try {
        setLoading(true);
        setError(null);
        setDebugInfo(null);

        // Get current user
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setError("User not found");
          return;
        }

        console.log("Current user ID:", user.id);

        // Fetch mentee record using user_uuid
        const { data: menteeData, error: menteeError } = await supabase
          .from("mentee")
          .select("uuid")
          .eq("user_uuid", user.id)
          .single();

        if (menteeError) {
          console.error("Mentee fetch error:", menteeError);
          setDebugInfo(`Mentee error: ${menteeError.message}`);
          setError("Mentee profile not found");
          return;
        }

        if (!menteeData) {
          setDebugInfo("No mentee data found for user");
          setError("Mentee profile not found");
          return;
        }

        console.log("Mentee UUID:", menteeData.uuid);

        // Fetch relationship assignments for this mentee
        const { data: assignments, error: assignmentsError } = await supabase
          .from("relationship_assignments")
          .select(
            `
            mentor_uuid,
            mentor:mentor (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `
          )
          .eq("mentee_uuid", menteeData.uuid);

        if (assignmentsError) {
          console.error("Assignments fetch error:", assignmentsError);
          setDebugInfo(`Assignments error: ${assignmentsError.message}`);
          setError("Failed to fetch mentors");
          return;
        }

        console.log("Assignments data:", assignments);

        if (assignments) {
          try {
            const mentorsList = (assignments as unknown as RawAssignmentData[])
              .filter((assignment) => assignment.mentor)
              .map((assignment) => ({
                id: assignment.mentor_uuid,
                name: assignment.mentor.vincer_base_user.name,
                email: assignment.mentor.vincer_base_user.email,
              }));
            console.log("Processed mentors:", mentorsList);
            setMentors(mentorsList);
          } catch (mapError) {
            console.error("Error processing mentors data:", mapError);
            setDebugInfo(
              `Data processing error: ${mapError instanceof Error ? mapError.message : String(mapError)}`
            );
            setError("Error processing mentor data");
          }
        }
      } catch (err) {
        console.error("Error fetching mentors:", err);
        setDebugInfo(
          `Unexpected error: ${err instanceof Error ? err.message : String(err)}`
        );
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchMentors();
  }, []);

  const handleMentorClick = (mentorId: string) => {
    navigate(`/mentee/mentors/${mentorId}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl">
        <h1 className="mb-8 text-3xl font-bold">My Mentors</h1>
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">Loading mentors...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl">
        <h1 className="mb-8 text-3xl font-bold">My Mentors</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <p className="font-medium">{error}</p>
          {debugInfo && <p className="mt-2 text-sm">{debugInfo}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h1 className="mb-8 text-3xl font-bold">My Mentors</h1>

      {mentors.length === 0 ? (
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">No mentors assigned yet.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {mentors.map((mentor) => (
            <div
              key={mentor.id}
              className="cursor-pointer rounded-xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              onClick={() => handleMentorClick(mentor.id)}
            >
              <div className="mb-4 flex items-center gap-4">
                <div className="rounded-lg bg-vincer-50 p-3">
                  <Users className="size-5 text-vincer-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium">{mentor.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="size-4" />
                    <span>{mentor.email}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="size-4" />
                <span>Available for sessions</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
